/** Page-wide click signals. Targets contain DOM positions, never labels or user data. */
export function observeClickFriction(report: (event: string, props: Record<string, string>) => void) {
  const history = new Map<string, number[]>();
  const reported = new Set<string>();
  let handlingClick = false;
  let clickTimer = 0;
  let cancelPending = () => {};
  const buttonSelector = 'button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]';
  const ignoredSelector = '[data-click-friction="ignore"], .ol-zoom, .maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out';

  function position(element: Element): string {
    const parts: string[] = [];
    let current: Element | null = element;
    while (current && parts.length < 12) {
      // Numeric sibling positions avoid collecting IDs, attributes, or content.
      const parent: Element | null = current.parentElement;
      const root = current.getRootNode();
      const siblings = parent?.children ?? (root instanceof ShadowRoot ? root.children : null);
      const index = siblings ? Array.prototype.indexOf.call(siblings, current) + 1 : 1;
      parts.unshift(String(index));
      current = parent ?? (root instanceof ShadowRoot ? root.host : null);
    }
    return parts.join('.');
  }

  function emit(event: string, target: string, control: string) {
    const key = `${event}:${control}`;
    if (reported.has(key) || reported.size >= 100) return;
    reported.add(key);
    report(event, { target, control });
  }

  function onClick(event: MouseEvent) {
    if (!event.isTrusted) return;
    cancelPending();
    const path = event.composedPath().filter((node): node is Element => node instanceof Element);
    if (path.some(node => node.matches(ignoredSelector))) return;
    const button = path.find(node => node.matches(buttonSelector));
    const element = button ?? path[0];
    if (!element) return;
    // Buttons over a map remain eligible; the drawing/panning surface does not.
    if (!button && path.some(node => node.matches('canvas, svg, #map, .ol-viewport, .maplibregl-map, input, textarea, select, label, a, [contenteditable], video, audio'))) return;
    if (button?.matches(':disabled, [aria-disabled="true"]')) return;
    handlingClick = true;
    window.clearTimeout(clickTimer);
    clickTimer = window.setTimeout(() => { handlingClick = false; }, 0);
    const control = position(element);
    const target = button ? 'button' : 'page';
    if (button) {
      const now = Date.now();
      const recent = (history.get(control) ?? []).filter(time => now - time <= 2000);
      recent.push(now);
      if (history.size >= 100 && !history.has(control)) history.clear();
      history.set(control, recent.slice(-3));
      if (recent.length >= 3) emit('button_rage_click_detected', target, control);
    }

    const href = location.href;
    const observer = new MutationObserver(records => {
      if (records.some(record => record.type !== 'attributes'
        || (record.target instanceof Element && record.attributeName !== null
          && record.oldValue !== record.target.getAttribute(record.attributeName)))) cancelPending();
    });
    const options = { childList: true, subtree: true, attributes: true, attributeOldValue: true, characterData: true, attributeFilter: ['class', 'disabled', 'aria-busy', 'aria-expanded', 'aria-pressed', 'open', 'hidden', 'value'] };
    observer.observe(document, options);
    // Lit controls render into shadow roots, outside the document observer.
    for (const node of path) {
      if (node.shadowRoot) observer.observe(node.shadowRoot, options);
    }
    const timer = window.setTimeout(() => {
      const unchanged = location.href === href && document.visibilityState === 'visible' && element.isConnected;
      cancelPending();
      if (unchanged) emit('suspected_dead_click', target, control);
    }, 2000);
    cancelPending = () => {
      observer.disconnect();
      window.clearTimeout(timer);
      cancelPending = () => {};
    };
  }

  const onFeedback = () => cancelPending();
  document.addEventListener('click', onClick, true);
  const feedbackEvents = ['input', 'change', 'submit', 'scroll', 'keydown', 'pointerdown', 'visibilitychange'];
  for (const name of feedbackEvents) document.addEventListener(name, onFeedback, true);
  window.addEventListener('pagehide', onFeedback);
  window.addEventListener('hashchange', onFeedback);
  return {
    isHandlingClick: () => handlingClick,
    dispose() {
      window.clearTimeout(clickTimer);
      cancelPending();
      document.removeEventListener('click', onClick, true);
      for (const name of feedbackEvents) document.removeEventListener(name, onFeedback, true);
      window.removeEventListener('pagehide', onFeedback);
      window.removeEventListener('hashchange', onFeedback);
      history.clear();
      reported.clear();
    },
  };
}
